import React, { useState } from 'react';
import {
  Clock, Users, IndianRupee, Split, Eye, Printer, AlertTriangle,
  CalendarPlus, CalendarClock, X, Phone, UserCheck, UserX,
  Ban, CheckCircle, Info, Timer, ChevronLeft, ChevronRight, CreditCard, Merge, Link2
} from 'lucide-react';
import { ConfirmDialog } from '../../Admin/Table_Management/UIComponents';

// Threshold for long-occupied table warning (in milliseconds)
const LONG_OCCUPATION_THRESHOLD = 2 * 60 * 60 * 1000; // 2 hours

// Format duration helper
const formatDuration = (mins) => {
  if (!mins) return '-';
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

export default function TableCard({
  table,
  splits = [],
  tableOrders = [],
  formatRunningTime,
  onClick,
  calculateOrderTotal,
  onViewOrders,
  onPrintBill,
  onSettleBill,
  onReserve,
  onReservationAction,
  onMerge,
}) {
  const [showReservationPopup, setShowReservationPopup] = useState(false);
  const [activeReservationTab, setActiveReservationTab] = useState(0);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [reservationToCancel, setReservationToCancel] = useState(null);

  const tableDisplayName = table.tableNumber;

  // Get ALL active reservations (not cancelled, completed, or no_show)
  const reservations = table.reservations || [];
  const activeReservations = reservations.filter(
    r => r.status !== 'cancelled' && r.status !== 'completed' && r.status !== 'no_show'
  );
  const blockedReservation = activeReservations.find(r => r.isBlocked);
  const hasReservations = activeReservations.length > 0;

  // ✅ Determine status from splits
  const activeSplits = splits.filter(s => s.status === 'active' || s.status === 'running');
  const heldSplits = splits.filter(s => s.status === 'hold' || s.status === 'HOLD' || s.status === 'HELD');
  const paymentPendingSplits = splits.filter(s => s.status === 'payment_pending');
  const mergedSplits = splits.filter(s => s.status === 'merged');
  const hasSplits = splits.length > 1;

  // Check if this table is merged (status='merged' in table or has merged split with masterTableId)
  const isMerged = table.status === 'merged' || mergedSplits.length > 0;

  // Find master table info from split data (now comes from API)
  const mergedSplit = mergedSplits[0] || splits.find(s => s.masterTableId);
  // masterTableName comes directly from API now, fallback to ID lookup
  const masterTableName = mergedSplit?.masterTableName ||
    (mergedSplit?.masterTableId ? `T${mergedSplit.masterTableId}` : '');

  // Determine card status
  const isReserved = !isMerged && (table.status === 'reserved' || !!blockedReservation);
  const isCurrent = false;

  // Payment pending takes priority - if ANY split is payment_pending, show it as payment_pending
  const isPaymentPending = !isMerged && paymentPendingSplits.length > 0;
  const isHeld = !isMerged && !isPaymentPending && splits.length > 0 && heldSplits.length === splits.length;
  const isOccupied = !isMerged && !isHeld && !isPaymentPending && activeSplits.length > 0;
  const isAvailable = !isMerged && !isOccupied && !isReserved && !isHeld && !isPaymentPending;

  const totalPax = table.totalGuests || tableOrders.reduce((sum, o) => sum + (o.pax || 0), 0);

  const totalBill = tableOrders.reduce((sum, order) => {
    if (order.amount !== undefined && order.amount !== null) {
      return sum + order.amount;
    }
    return sum + (calculateOrderTotal ? calculateOrderTotal(order) : 0);
  }, 0);

  const firstOrderTime = tableOrders.length > 0
    ? [...tableOrders].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[0].createdAt
    : null;

  const isLongOccupied = (() => {
    if (!firstOrderTime || (!isOccupied && !isHeld)) return false;
    const elapsed = Date.now() - new Date(firstOrderTime).getTime();
    return elapsed > LONG_OCCUPATION_THRESHOLD;
  })();

  // STYLES
  const getCardStyle = () => {
    if (isMerged) {
      return 'bg-gradient-to-br from-slate-100 to-slate-200 text-slate-600 border-slate-400';
    }
    if (isCurrent) {
      return 'bg-gradient-to-br from-teal-500 to-teal-600 text-white border-teal-600';
    }
    if (isPaymentPending) {
      return 'bg-gradient-to-br from-pink-50 to-rose-50 text-pink-900 border-pink-400';
    }
    if (isHeld) {
      return 'bg-gradient-to-br from-amber-50 to-amber-100 text-amber-900 border-amber-400';
    }
    if (isReserved) {
      return 'bg-gradient-to-br from-blue-50 to-blue-100 text-blue-900 border-blue-400';
    }
    if (isLongOccupied) {
      return 'bg-gradient-to-br from-red-50 to-orange-50 text-red-900 border-red-400';
    }
    if (isOccupied) {
      return 'bg-gradient-to-br from-indigo-50 to-indigo-100 text-indigo-900 border-indigo-400';
    }
    return 'bg-white text-emerald-900 border-emerald-400';
  };

  const getHoverEffect = () => {
    // Merged and Reserved tables are not clickable
    if (isMerged) return 'cursor-not-allowed opacity-75';
    if (isReserved) return 'cursor-default opacity-95 hover:shadow-md';
    return 'cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:border-opacity-100';
  };

  const getStatusBadge = () => {
    if (isMerged)
      return (
        <span className="px-2 py-0.5 bg-slate-500 text-white text-[10px] font-bold rounded uppercase flex items-center gap-1">
          <Link2 size={10} />
          Merged
        </span>
      );
    if (isCurrent)
      return <span className="px-2 py-0.5 bg-white/20 text-white text-[10px] font-bold rounded uppercase">Active</span>;
    if (isPaymentPending)
      return (
        <span className="px-2 py-0.5 bg-pink-600 text-white text-[10px] font-bold rounded uppercase flex items-center gap-1">
          <CreditCard size={10} />
          Pay
        </span>
      );
    if (isHeld)
      return <span className="px-2 py-0.5 bg-amber-600 text-white text-[10px] font-bold rounded uppercase">Hold</span>;
    if (isLongOccupied)
      return (
        <span className="px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded uppercase flex items-center gap-1 animate-pulse">
          <AlertTriangle size={10} />
          2h+
        </span>
      );
    if (isOccupied && !isHeld)
      return <span className="px-2 py-0.5 bg-indigo-600 text-white text-[10px] font-bold rounded uppercase">Running</span>;
    return null;
  };

  // Handle reservation action - pass table for seated action to start order
  const handleReservationAction = (action, reservation) => {
    if (onReservationAction) {
      onReservationAction(action, reservation, table);
    }
    setShowReservationPopup(false);
  };

  // Handle cancel with confirmation
  const handleCancelClick = (reservation) => {
    setReservationToCancel(reservation);
    setShowCancelConfirm(true);
  };

  const confirmCancel = () => {
    if (reservationToCancel) {
      handleReservationAction('cancel', reservationToCancel);
    }
    setShowCancelConfirm(false);
    setReservationToCancel(null);
  };

  // Open popup handler
  const openReservationPopup = (e) => {
    e.stopPropagation();
    setActiveReservationTab(0);
    setShowReservationPopup(true);
  };

  // Multi-Reservation Popup Component (Fixed Position Modal)
  const ReservationModal = () => {
    if (!hasReservations) return null;

    const currentReservation = activeReservations[activeReservationTab];
    const hasPrev = activeReservationTab > 0;
    const hasNext = activeReservationTab < activeReservations.length - 1;
    const hasMultiple = activeReservations.length > 1;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowReservationPopup(false)}>
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50" />

        {/* Modal */}
        <div
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-white">
                <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
                  <CalendarClock size={22} />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Table {tableDisplayName}</h3>
                  <p className="text-teal-100 text-sm">
                    {hasMultiple ? `${activeReservations.length} Reservations` : 'Reservation Details'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Cancel Reservation - in header */}
                {currentReservation && (currentReservation.status === 'confirmed' || currentReservation.status === 'seated') && (
                  <button
                    onClick={() => handleCancelClick(currentReservation)}
                    className="px-3 py-1.5 flex items-center gap-1.5 bg-white/20 hover:bg-red-500 text-white rounded-lg text-xs font-semibold transition-all"
                    title="Cancel Reservation"
                  >
                    <Ban size={14} />
                    <span>Cancel</span>
                  </button>
                )}
                {/* Close button */}
                <button
                  onClick={() => setShowReservationPopup(false)}
                  className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-lg transition-colors"
                  title="Close"
                >
                  <X size={18} className="text-white" />
                </button>
              </div>
            </div>
          </div>

          {/* Time Tabs with Navigation - Only for multiple reservations */}
          {hasMultiple && (
            <div className="bg-slate-50 border-b border-slate-200">
              <div className="flex items-center">
                {/* Left Arrow */}
                <button
                  onClick={() => hasPrev && setActiveReservationTab(prev => prev - 1)}
                  className={`
                    flex-shrink-0 w-10 h-12 flex items-center justify-center border-r border-slate-200 transition-all
                    ${hasPrev
                      ? 'text-teal-600 hover:bg-teal-50 cursor-pointer'
                      : 'text-slate-300 cursor-not-allowed'}
                  `}
                >
                  <ChevronLeft size={20} />
                </button>

                {/* Time Tabs */}
                <div className="flex-1 flex items-center justify-center gap-1 px-2 py-2">
                  {activeReservations.map((res, idx) => (
                    <button
                      key={res.id}
                      onClick={() => setActiveReservationTab(idx)}
                      className={`
                        relative px-3 py-1.5 rounded-lg text-sm font-semibold transition-all
                        ${activeReservationTab === idx
                          ? 'bg-teal-500 text-white shadow-sm'
                          : 'text-slate-600 hover:bg-slate-200'}
                      `}
                    >
                      <div className="flex items-center gap-1.5">
                        <Clock size={12} />
                        <span>{res.timeDisplay}</span>
                      </div>
                      {res.isBlocked && (
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-rose-500 rounded-full border-2 border-white" />
                      )}
                    </button>
                  ))}
                </div>

                {/* Right Arrow */}
                <button
                  onClick={() => hasNext && setActiveReservationTab(prev => prev + 1)}
                  className={`
                    flex-shrink-0 w-10 h-12 flex items-center justify-center border-l border-slate-200 transition-all
                    ${hasNext
                      ? 'text-teal-600 hover:bg-teal-50 cursor-pointer'
                      : 'text-slate-300 cursor-not-allowed'}
                  `}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          )}

          {/* Content */}
          {currentReservation && (
            <div className="p-5 space-y-4">
              {/* Blocked indicator */}
              {currentReservation.isBlocked && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm font-medium">
                  <div className="w-2 h-2 bg-rose-500 rounded-full animate-pulse" />
                  <span>Table blocked for this reservation</span>
                </div>
              )}

              {/* Customer Info */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-teal-200 rounded-xl flex items-center justify-center text-teal-600 font-bold text-lg">
                  {currentReservation.customerName?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 truncate">{currentReservation.customerName || 'Guest'}</p>
                  <div className="flex items-center gap-1.5 text-sm text-slate-500">
                    <Phone size={12} />
                    <span>{currentReservation.customerPhone || 'No phone'}</span>
                  </div>
                </div>
              </div>

              {/* Reservation Details */}
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-slate-50 rounded-xl p-2.5 text-center">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">Time</p>
                  <p className="font-bold text-slate-800 text-sm mt-0.5">{currentReservation.timeDisplay}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-2.5 text-center">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">Guests</p>
                  <p className="font-bold text-slate-800 text-sm mt-0.5">{currentReservation.guestCount}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-2.5 text-center">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">Duration</p>
                  <p className="font-bold text-slate-800 text-sm mt-0.5">{formatDuration(currentReservation.expectedDurationMins)}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-2.5 text-center">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">Status</p>
                  <p className={`font-bold text-sm mt-0.5 capitalize ${
                    currentReservation.status === 'confirmed' ? 'text-teal-600' :
                    currentReservation.status === 'seated' ? 'text-emerald-600' :
                    'text-slate-600'
                  }`}>{currentReservation.status}</p>
                </div>
              </div>

              {/* Notes */}
              {currentReservation.notes && (
                <div className="bg-amber-50/50 border border-amber-100 rounded-xl p-3">
                  <p className="text-[10px] text-amber-500 uppercase font-semibold mb-1">Notes</p>
                  <p className="text-sm text-amber-700">{currentReservation.notes}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="pt-3 border-t border-slate-100">
                {currentReservation.status === 'confirmed' && (
                  <div className="grid grid-cols-2 gap-2.5">
                    <button
                      onClick={() => handleReservationAction('seated', currentReservation)}
                      className="flex items-center justify-center gap-2 px-3 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold text-sm transition-all"
                    >
                      <UserCheck size={16} />
                      Seated
                    </button>
                    <button
                      onClick={() => handleReservationAction('no_show', currentReservation)}
                      className="flex items-center justify-center gap-2 px-3 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold text-sm transition-all"
                    >
                      <UserX size={16} />
                      No Show
                    </button>
                  </div>
                )}

                {currentReservation.status === 'seated' && (
                  <button
                    onClick={() => handleReservationAction('completed', currentReservation)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold text-sm transition-all"
                  >
                    <CheckCircle size={16} />
                    Mark Completed
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <div
        onClick={isMerged || isReserved ? undefined : onClick}
        title={isMerged ? `Merged with ${masterTableName}` : undefined}
        className={`
          group relative p-2.5 rounded-lg font-semibold text-left transition-all duration-200
          border-2 border-dashed ${getCardStyle()} ${getHoverEffect()}
          shadow-sm w-full flex flex-col justify-between
        `}
      >
        {/* Header: Table Name (with API splits) */}
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <span className="text-base font-black">{tableDisplayName}</span>

            {hasSplits && (
              <div className="flex items-center gap-0.5 px-1 py-0.5 bg-black/10 rounded text-[10px] font-bold">
                <Split size={10} />
                <span>{activeSplits.length}</span>
              </div>
            )}
          </div>
          {getStatusBadge()}
        </div>

        {/* Content */}
        <div className="flex-1 space-y-1">

          {/* Merged - Show master table info */}
          {isMerged && (
            <div className="flex items-center justify-center py-2">
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <Link2 size={12} />
                <span>Merged with</span>
                <span className="font-bold text-slate-700 text-sm">{masterTableName || 'another table'}</span>
              </div>
            </div>
          )}

          {/* Reserved - Show blocking reservation details */}
          {isReserved && blockedReservation && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-bold text-blue-700">
                <Clock size={12} />
                <span>{blockedReservation.timeDisplay}</span>
                <span className="text-blue-500">•</span>
                <span className="flex items-center gap-1">
                  <Timer size={10} />
                  {formatDuration(blockedReservation.expectedDurationMins)}
                </span>
              </div>
              <div className="text-[11px] font-semibold text-blue-600 truncate">
                {blockedReservation.customerName}
              </div>
              <div className="flex items-center gap-2 text-[10px] text-blue-600/70">
                <span className="flex items-center gap-0.5">
                  <Users size={10} />
                  {blockedReservation.guestCount}
                </span>
                <span className="flex items-center gap-0.5">
                  <Phone size={10} />
                  {blockedReservation.customerPhone}
                </span>
              </div>
            </div>
          )}

          {/* Reserved without details (legacy) */}
          {isReserved && !blockedReservation && (
            <div className="text-sm font-semibold opacity-70">
              <Clock size={14} className="inline mr-1" />
              Reserved
            </div>
          )}

          {/* Occupied/Held/Payment Pending */}
          {(isOccupied || isHeld || isPaymentPending) && (
            <>
              {/* Time + Pax + Bill — single compact row */}
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className={`flex items-center gap-0.5 ${isLongOccupied ? 'text-red-600 font-black' : 'opacity-80'}`}>
                    <Clock size={12} className={isLongOccupied ? 'animate-pulse' : ''} />
                    <span className="font-bold">{formatRunningTime(firstOrderTime)}</span>
                  </div>
                  <div className="flex items-center gap-0.5 opacity-80">
                    <Users size={12} />
                    <span className="font-bold">{totalPax}</span>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 text-sm font-black">
                  <IndianRupee size={12} />
                  <span>{totalBill.toFixed(0)}</span>
                </div>
              </div>

              {/* SPLIT DETAILS */}
              {hasSplits && (
                <div className="pt-1 border-t border-current/20">
                  <div className="space-y-0.5">
                    {splits.map((split, idx) => {
                      const runningOrder = tableOrders.find(
                        order => order.tableSplit === split.splitName ||
                                 order.tableSplit === split.splitName?.replace('Split ', '')
                      );
                      const splitAmount = runningOrder?.amount || 0;
                      const isSplitHeld = split.status === 'hold' || split.status === 'HOLD' || split.status === 'HELD';
                      const isSplitPaymentPending = split.status === 'payment_pending';

                      return (
                        <div
                          key={split.splitId || idx}
                          className="flex items-center justify-between gap-1 px-1.5 py-0.5 bg-black/10 rounded text-[10px] font-bold overflow-hidden"
                        >
                          <div className="flex items-center gap-1 min-w-0 truncate">
                            <span className="shrink-0">{split.splitName?.replace('Split ', '') || split.splitName}</span>
                            {runningOrder && (
                              <>
                                <span className="opacity-40">·</span>
                                <Clock size={9} className="opacity-60 shrink-0" />
                                <span className="shrink-0">{formatRunningTime(runningOrder.createdAt)}</span>
                              </>
                            )}
                            <span className="opacity-40">·</span>
                            <Users size={9} className="opacity-60 shrink-0" />
                            <span className="shrink-0">{split.peopleCount || 0}</span>
                            {splitAmount > 0 && (
                              <>
                                <span className="opacity-40">·</span>
                                <span className="shrink-0">₹{splitAmount.toFixed(0)}</span>
                              </>
                            )}
                          </div>

                          {isSplitHeld && (
                            <span className="px-1 py-0.5 bg-amber-600 text-white text-[8px] font-bold rounded uppercase shrink-0">
                              HOLD
                            </span>
                          )}
                          {isSplitPaymentPending && (
                            <span className="px-1 py-0.5 bg-pink-600 text-white text-[8px] font-bold rounded uppercase flex items-center gap-0.5 shrink-0">
                              <CreditCard size={8} />
                              PAY
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Warning if table has upcoming reservation while occupied */}
              {hasReservations && !isReserved && (
                <div className="pt-1 border-t border-current/20">
                  <div
                    className="flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 rounded px-1.5 py-1 cursor-pointer hover:bg-amber-100 transition-colors"
                    onClick={openReservationPopup}
                  >
                    <AlertTriangle size={12} />
                    <span>
                      {activeReservations.length} upcoming reservation{activeReservations.length > 1 ? 's' : ''}
                    </span>
                    <Info size={10} className="ml-auto" />
                  </div>
                </div>
              )}
            </>
          )}

          {/* AVAILABLE */}
          {isAvailable && (
            <div className="space-y-0.5">
              <div className="text-xs font-semibold text-emerald-600">
                Ready for Order <span className="text-[10px] font-normal opacity-60">(Cap: {table.seatingCapacity})</span>
              </div>
              {/* Show upcoming reservation warning */}
              {hasReservations && (
                <div
                  className="pt-1 border-t border-emerald-200 cursor-pointer"
                  onClick={openReservationPopup}
                >
                  <div className="flex items-center justify-between gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 rounded px-1.5 py-1 hover:bg-amber-100 transition-colors">
                    <div className="flex items-center gap-1">
                      <CalendarClock size={10} />
                      <span>
                        {activeReservations.length} reservation{activeReservations.length > 1 ? 's' : ''} today
                      </span>
                    </div>
                    <Info size={10} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Hover Action Buttons - Merge (for all tables) & Reserve (for available/reserved) */}
        {(isAvailable || isReserved || isOccupied || isHeld) && (onReserve || onMerge) && (
          <div
            className={`absolute top-2 ${hasReservations ? 'right-8' : 'right-2'} z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-1.5`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Merge Button - For available and occupied tables */}
            {(isAvailable || isOccupied || isHeld) && onMerge && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMerge(table, isAvailable ? 'available' : 'occupied');
                }}
                className="w-6 h-6 bg-teal-500 hover:bg-teal-600 text-white rounded shadow-md transition-all hover:scale-110 flex items-center justify-center"
                title={isAvailable ? "Merge with other tables" : "Add tables to this order"}
              >
                <Merge size={12} />
              </button>
            )}
            {/* Reserve Button - Only for available/reserved tables */}
            {(isAvailable || isReserved) && onReserve && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReserve(table);
                }}
                className="w-6 h-6 bg-indigo-500 hover:bg-indigo-600 text-white rounded shadow-md transition-all hover:scale-110 flex items-center justify-center"
                title={hasReservations ? "Add another reservation" : "Reserve this table"}
              >
                <CalendarPlus size={12} />
              </button>
            )}
          </div>
        )}

        {/* Action Buttons - Only for single split occupied/held/payment_pending tables */}
        {(isOccupied || isHeld || isPaymentPending) && splits.length === 1 && onViewOrders && (onPrintBill || onSettleBill) && (
          <div
            className="absolute -top-2.5 left-1/2 -translate-x-1/2 flex gap-1.5 z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                const split = splits[0];
                const splitName = split.splitName?.replace('Split ', '') || split.splitName;
                onViewOrders(table.id, splitName, tableDisplayName);
              }}
              className="px-2 py-1 bg-teal-600 hover:bg-teal-700 text-white text-[10px] font-bold rounded shadow-lg transition-all hover:scale-105 flex items-center gap-1"
              title="View active orders"
            >
              <Eye size={11} />
              <span>Orders</span>
            </button>
            {/* Show Settle button for payment_pending, Print button otherwise */}
            {isPaymentPending && onSettleBill ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const split = splits[0];
                  const splitName = split.splitName?.replace('Split ', '') || split.splitName;
                  onSettleBill(table.id, splitName, split.peopleCount || 2);
                }}
                className="px-2 py-1 bg-pink-600 hover:bg-pink-700 text-white text-[10px] font-bold rounded shadow-lg transition-all hover:scale-105"
                title="Settle payment"
              >
                Settle
              </button>
            ) : onPrintBill && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const split = splits[0];
                  const splitName = split.splitName?.replace('Split ', '') || split.splitName;
                  onPrintBill(table.id, splitName, split.peopleCount || 2);
                }}
                className="px-2 py-1 bg-cyan-600 hover:bg-cyan-700 text-white text-[10px] font-bold rounded shadow-lg transition-all hover:scale-105 flex items-center gap-1"
                title="Print bill"
              >
                <Printer size={11} />
                <span>Print</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Reservation Icon - Shows reservation count (OUTSIDE button) */}
      {hasReservations && (
        <div
          className="absolute -top-1.5 -right-1.5 z-30 cursor-pointer"
          onClick={openReservationPopup}
        >
          <div className="relative">
            <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center shadow-md cursor-pointer hover:scale-110 hover:bg-teal-600 transition-all">
              {activeReservations.length > 1 ? (
                <span className="text-white text-[10px] font-bold">{activeReservations.length}</span>
              ) : (
                <CalendarClock size={12} className="text-white" />
              )}
            </div>
            {/* Pulse animation for blocked reservation */}
            {blockedReservation && (
              <div className="absolute inset-0 w-6 h-6 bg-teal-400 rounded-full animate-ping opacity-50"></div>
            )}
          </div>
        </div>
      )}

      {/* Reservation Modal */}
      {showReservationPopup && <ReservationModal />}

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => {
          setShowCancelConfirm(false);
          setReservationToCancel(null);
        }}
        onConfirm={confirmCancel}
        title="Cancel Reservation"
        message={reservationToCancel
          ? `Are you sure you want to cancel the reservation for ${reservationToCancel.customerName || 'this customer'} at ${reservationToCancel.timeDisplay}?`
          : 'Are you sure you want to cancel this reservation?'
        }
        confirmText="Yes, Cancel"
        type="danger"
      />
    </div>
  );
}
